import React, { useEffect, useState, useContext } from 'react';
import { collection, getDocs, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import db from '../firebase'; // Import your Firestore instance
import { FeedbackContext } from '../contexts/FeedbackContext';
import '../styles/admin.css';
import ArticleCountGraph from './ArticleCountGraph'; // Import the graph component
import { updateArticleCountStats } from '../utils/articleAnalytics'; // Import the function to update article stats
import {
    Grid,
    Box,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Paper,
    Container
} from '@mui/material';
import { 
    Delete as DeleteIcon, 
    AdminPanelSettings as AdminPanelSettingsIcon,
    CheckCircle as CheckCircleIcon,
    Block as BlockIcon,
    PersonOff as PersonOffIcon
} from '@mui/icons-material';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth } from '../firebase';

console.log('Signed in user:', auth.currentUser?.uid);


function Admin() {
    const [users, setUsers] = useState([]);
    const { showFeedback } = useContext(FeedbackContext);
    const [isUpdating, setIsUpdating] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [graphRefreshTrigger, setGraphRefreshTrigger] = useState(0);


    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const querySnapshot = await getDocs(collection(db, 'users'));
                const usersList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setUsers(usersList);
            } catch (error) {
                showFeedback(`Error fetching users: ${error.message}`, 'error');
            }
        };

        fetchUsers();
    }, [showFeedback]);


    const approveUser = async (userId) => {
        try {
            await updateDoc(doc(db, 'users', userId), {
                isApproved: true,
            });
            setUsers(users.map(user => user.id === userId ? { ...user, isApproved: true } : user));
            showFeedback('User approved successfully.', 'success');
        } catch (error) {
            showFeedback(`Error approving user: ${error.message}`, 'error');
        }
    };

    const makeAdmin = async (userId) => {
        try {
            await updateDoc(doc(db, 'users', userId), {
                isAdmin: true,
            });
            setUsers(users.map(user => user.id === userId ? { ...user, isAdmin: true } : user));
            showFeedback('User made admin successfully.', 'success');
        } catch (error) {
            showFeedback(`Error making user admin: ${error.message}`, 'error');
        }
    };

    const deleteAuthUser = async (uid) => {
        const functions = getFunctions();
        const deleteUserFn = httpsCallable(functions, 'deleteAuthUser');

        try {
            await deleteUserFn({ uid });
            showFeedback('Auth user deleted successfully.', 'success');
        } catch (error) {
            showFeedback(`Failed to delete auth user: ${error.message}`, 'error');
        }
    };

    const deleteUser = async (userId) => {
        try {
            await deleteDoc(doc(db, 'users', userId));
            setUsers(users.filter(user => user.id !== userId));
            await deleteAuthUser(userId); // Call the function to delete the auth user
            showFeedback('User deleted successfully.', 'success');
        } catch (error) {
            showFeedback(`Error deleting user: ${error.message}`, 'error');
        }
    };

    const handleUpdateStats = async () => {
        setIsUpdating(true);
        try {
            await updateArticleCountStats();
            showFeedback('Article statistics updated successfully');
            // Trigger graph refresh by incrementing the state
            setGraphRefreshTrigger(prev => prev + 1);
        } catch (error) {
            console.error('Failed to update stats:', error);
            showFeedback('Failed to update article statistics', 'error');
        } finally {
            setIsUpdating(false);
        }
    };

    const handleDeleteClick = (user) => {
        setUserToDelete(user);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (userToDelete) {
            await deleteUser(userToDelete.id);
            setDeleteDialogOpen(false);
            setUserToDelete(null);
        }
    };

    // Add new functions after existing user management functions
    const banUser = async (userId) => {
        try {
            await updateDoc(doc(db, 'users', userId), {
                isApproved: false,
            });
            setUsers(users.map(user =>
                user.id === userId ? { ...user, isApproved: false } : user
            ));
            showFeedback('User banned successfully.', 'success');
        } catch (error) {
            showFeedback(`Error banning user: ${error.message}`, 'error');
        }
    };

    const removeAdmin = async (userId) => {
        try {
            await updateDoc(doc(db, 'users', userId), {
                isAdmin: false,
            });
            setUsers(users.map(user =>
                user.id === userId ? { ...user, isAdmin: false } : user
            ));
            showFeedback('Admin privileges removed successfully.', 'success');
        } catch (error) {
            showFeedback(`Error removing admin privileges: ${error.message}`, 'error');
        }
    };

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Grid container spacing={3}>
                {/* User Management Section */}
                <Grid item xs={12}>
                    <Paper sx={{ p: 3, mb: 3 }}>
                        <Typography variant="h5" sx={{ mb: 3 }}>
                            User Management
                        </Typography>
                        <Box sx={{ width: '100%' }}>
                            {users.map(user => (
                                <Paper
                                    key={user.id}
                                    elevation={2}
                                    sx={{
                                        p: 2,
                                        mb: 2,
                                        display: 'flex',
                                        flexDirection: { xs: 'column', sm: 'row' },
                                        gap: { xs: 2, sm: 0 },
                                        justifyContent: 'space-between',
                                        alignItems: { xs: 'stretch', sm: 'center' }
                                    }}
                                >
                                    <Box>
                                        <Typography variant="body1">{user.email}</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Status: {user.isApproved ? 'Approved' : 'Pending'} •
                                            Role: {user.isAdmin ? 'Admin' : 'User'}
                                        </Typography>
                                    </Box>
                                    <Box sx={{
                                        display: 'flex',
                                        gap: 1,
                                        flexDirection: { xs: 'column', sm: 'row' },
                                        width: { xs: '100%', sm: 'auto' }
                                    }}>
                                        {!user.isApproved && (
                                            <Button
                                                fullWidth
                                                startIcon={<CheckCircleIcon />}
                                                variant="outlined"
                                                onClick={() => approveUser(user.id)}
                                            >
                                                Approve
                                            </Button>
                                        )}
                                        {user.isApproved && !user.isAdmin && (
                                            <Button
                                                fullWidth
                                                color="warning"
                                                startIcon={<BlockIcon />}
                                                variant="outlined"
                                                onClick={() => banUser(user.id)}
                                            >
                                                Ban
                                            </Button>
                                        )}
                                        {!user.isAdmin && (
                                            <Button
                                                fullWidth
                                                startIcon={<AdminPanelSettingsIcon />}
                                                variant="outlined"
                                                onClick={() => makeAdmin(user.id)}
                                                disabled={!user.isApproved}
                                            >
                                                Make Admin
                                            </Button>
                                        )}
                                        {user.isAdmin && user.id !== auth.currentUser?.uid && (
                                            <Button
                                                fullWidth
                                                color="warning"
                                                startIcon={<PersonOffIcon />}
                                                variant="outlined"
                                                onClick={() => removeAdmin(user.id)}
                                            >
                                                Remove Admin
                                            </Button>
                                        )}
                                        <Button
                                            fullWidth
                                            color="error"
                                            startIcon={<DeleteIcon />}
                                            variant="outlined"
                                            onClick={() => handleDeleteClick(user)}
                                            disabled={user.id === auth.currentUser?.uid}
                                        >
                                            Delete
                                        </Button>
                                    </Box>
                                </Paper>
                            ))}
                        </Box>
                    </Paper>
                </Grid>

                {/* Analytics Section */}
                <Grid item xs={12}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h5" sx={{ mb: 3 }}>
                            Article Analytics
                        </Typography>
                        <Button
                            variant="contained"
                            onClick={handleUpdateStats}
                            disabled={isUpdating}
                            sx={{ mb: 3 }}
                        >
                            {isUpdating ? 'Updating Stats...' : 'Update Article Stats'}
                        </Button>
                        <Box sx={{ height: 400 }}>
                            <ArticleCountGraph refreshTrigger={graphRefreshTrigger}/>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
            >
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete user {userToDelete?.email}?
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setDeleteDialogOpen(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        color="error"
                        variant="contained"
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}

export default Admin;